import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useExpenses } from "@/contexts/expense-context";
import { BottomNav } from "@/components/bottom-nav";
import { SUPPORTED_CURRENCIES } from "@/lib/utils/currency";
import { ChevronRight, Download, Repeat } from "lucide-react";
import { ExportService } from "@/lib/export-service";
import { Skeleton } from "@/components/skeleton";

export default function SettingsPage() {
    const navigate = useNavigate();
    const { expenses, defaultCurrency, updateDefaultCurrency, loading, error } = useExpenses();
    const [selectedCurrency, setSelectedCurrency] = useState(defaultCurrency);
    const [isSaving, setIsSaving] = useState(false);
    const [successMessage, setSuccessMessage] = useState("");
    const [isExporting, setIsExporting] = useState(false);

    const handleSave = async () => {
        if (selectedCurrency === defaultCurrency) return;

        setIsSaving(true);
        setSuccessMessage("");

        try {
            await updateDefaultCurrency(selectedCurrency);
            setSuccessMessage("Default currency updated successfully!");
            setTimeout(() => setSuccessMessage(""), 3000);
        } catch (err) {
            // Error is already set in context
        } finally {
            setIsSaving(false);
        }
    };

    const handleExportCSV = () => {
        setIsExporting(true);
        setSuccessMessage("");

        try {
            const result = ExportService.exportToCSV(expenses);
            setSuccessMessage(`✓ Exported ${result.recordCount} expenses to ${result.filename}`);
            setTimeout(() => setSuccessMessage(""), 5000);
        } catch (err) {
            setSuccessMessage("Failed to export expenses. Please try again.");
        } finally {
            setIsExporting(false);
        }
    };

    const handleExportCurrentMonth = () => {
        setIsExporting(true);
        setSuccessMessage("");

        try {
            const result = ExportService.exportCurrentMonth(expenses);
            setSuccessMessage(`✓ Exported ${result.recordCount} expenses for current month`);
            setTimeout(() => setSuccessMessage(""), 5000);
        } catch (err) {
            setSuccessMessage("Failed to export expenses. Please try again.");
        } finally {
            setIsExporting(false);
        }
    };

    const handleExportCurrentYear = () => {
        setIsExporting(true);
        setSuccessMessage("");

        try {
            const result = ExportService.exportCurrentYear(expenses);
            setSuccessMessage(`✓ Exported ${result.recordCount} expenses for current year`);
            setTimeout(() => setSuccessMessage(""), 5000);
        } catch (err) {
            setSuccessMessage("Failed to export expenses. Please try again.");
        } finally {
            setIsExporting(false);
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 pb-20">
            {/* Header */}
            <div className="bg-white border-b border-gray-200">
                <div className="max-w-4xl mx-auto px-4 py-6">
                    <h1 className="text-3xl font-bold text-gray-900">Settings</h1>
                    <p className="text-gray-600 mt-1">Customize your expense tracking preferences</p>
                </div>
            </div>

            {/* Main Content */}
            <div className="max-w-4xl mx-auto px-4 py-8">
                {/* Error Message */}
                {error && (
                    <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                        {error}
                    </div>
                )}

                {/* Success Message */}
                {successMessage && (
                    <div className="mb-6 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg">
                        {successMessage}
                    </div>
                )}

                {/* Default Currency Setting */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
                    <h2 className="text-lg font-semibold text-gray-900 mb-2">Default Currency</h2>
                    <p className="text-sm text-gray-600 mb-4">
                        This currency will be pre-selected when adding new expenses.
                    </p>

                    {loading ? (
                        <Skeleton className="h-10 w-48" />
                    ) : (
                        <div className="space-y-4">
                            <div>
                                <label htmlFor="currency" className="block text-sm font-medium text-gray-700 mb-2">
                                    Select Currency
                                </label>
                                <select
                                    id="currency"
                                    value={selectedCurrency}
                                    onChange={(e) => setSelectedCurrency(e.target.value)}
                                    className="w-full max-w-xs px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                                >
                                    {SUPPORTED_CURRENCIES.map((currency) => (
                                        <option key={currency} value={currency}>
                                            {currency}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <button
                                onClick={handleSave}
                                disabled={isSaving || selectedCurrency === defaultCurrency}
                                className="bg-teal-600 hover:bg-teal-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-semibold py-2 px-6 rounded-lg transition-colors"
                            >
                                {isSaving ? "Saving..." : "Save Changes"}
                            </button>
                        </div>
                    )}
                </div>

                {/* Data Management Section */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
                    <h2 className="text-lg font-semibold text-gray-900 mb-2">Data Management</h2>
                    <p className="text-sm text-gray-600 mb-4">
                        Export your expense data for backup or tax purposes
                    </p>

                    <div className="space-y-3">
                        <button
                            onClick={handleExportCSV}
                            disabled={isExporting || expenses.length === 0}
                            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-teal-600 hover:bg-teal-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-colors"
                        >
                            <Download size={20} />
                            {isExporting ? "Exporting..." : `Export All Expenses to CSV (${expenses.length})`}
                        </button>

                        <div className="grid grid-cols-2 gap-3">
                            <button
                                onClick={handleExportCurrentMonth}
                                disabled={isExporting || expenses.length === 0}
                                className="flex items-center justify-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 disabled:bg-gray-50 disabled:cursor-not-allowed text-gray-700 text-sm font-medium rounded-lg transition-colors"
                            >
                                <Download size={16} />
                                This Month
                            </button>
                            <button
                                onClick={handleExportCurrentYear}
                                disabled={isExporting || expenses.length === 0}
                                className="flex items-center justify-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 disabled:bg-gray-50 disabled:cursor-not-allowed text-gray-700 text-sm font-medium rounded-lg transition-colors"
                            >
                                <Download size={16} />
                                This Year
                            </button>
                        </div>
                    </div>

                    {expenses.length === 0 && (
                        <p className="text-sm text-gray-500 mt-3 text-center">
                            No expenses to export. Add some expenses first!
                        </p>
                    )}

                    <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                        <p className="text-sm text-blue-900 flex items-start gap-2">
                            <span>💡</span>
                            <span>
                                <strong>Tip:</strong> Export monthly for tax purposes. CSV files open in Excel, Google Sheets, or any spreadsheet app.
                            </span>
                        </p>
                    </div>
                </div>

                {/* Category Management Link */}
                <button
                    onClick={() => navigate('/settings/categories')}
                    className="w-full bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6 hover:bg-gray-50 transition-colors text-left"
                >
                    <div className="flex items-center justify-between">
                        <div>
                            <h2 className="text-lg font-semibold text-gray-900 mb-1">Manage Categories</h2>
                            <p className="text-sm text-gray-600">
                                Customize expense categories, hide unused ones, or create your own
                            </p>
                        </div>
                        <ChevronRight className="text-gray-400" size={24} />
                    </div>
                </button>

                {/* Recurring Expenses Link */}
                <button
                    onClick={() => navigate('/settings/recurring')}
                    className="w-full bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:bg-gray-50 transition-colors text-left"
                >
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <Repeat className="w-6 h-6 text-teal-600" />
                            <div>
                                <h2 className="text-lg font-semibold text-gray-900 mb-1">Recurring Expenses</h2>
                                <p className="text-sm text-gray-600">
                                    Manage automatic expense templates for bills and subscriptions
                                </p>
                            </div>
                        </div>
                        <ChevronRight className="text-gray-400" size={24} />
                    </div>
                </button>
            </div>

            {/* Bottom Navigation */}
            <BottomNav />
        </div>
    );
}
